import axios, {
  AxiosError,
  AxiosHeaders,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { RefreshResponse } from '../types';

export const API_BASE =
  import.meta.env.VITE_API_BASE ?? 'http://localhost:8080/api/v1';

// ---------------------------------------------------------------------------
// Token storage (localStorage) — small, framework-free helper so both the
// axios interceptors and the AuthContext read/write the same keys.
// ---------------------------------------------------------------------------

const ACCESS_KEY = 'pos.accessToken';
const REFRESH_KEY = 'pos.refreshToken';
const USER_KEY = 'pos.user';

export const tokenStore = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  getUser: () => localStorage.getItem(USER_KEY),
  setAccess: (t: string) => localStorage.setItem(ACCESS_KEY, t),
  setRefresh: (t: string) => localStorage.setItem(REFRESH_KEY, t),
  setUser: (json: string) => localStorage.setItem(USER_KEY, json),
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

// Called by AuthContext when a session becomes invalid, to force a redirect.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// A bare axios instance used for the refresh call so it does not recurse
// through the response interceptor below.
const refreshClient = axios.create({ baseURL: API_BASE });

// --------------------------- Request interceptor --------------------------

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.getAccess();
  if (token) {
    const headers = AxiosHeaders.from(config.headers);
    headers.set('Authorization', `Bearer ${token}`);
    config.headers = headers;
  }
  return config;
});

// --------------------------- Response interceptor -------------------------
// On 401, attempt a single refresh, then replay the original request.
// If refresh fails (or there is no refresh token), clear the session and
// hand off to the unauthorized handler (redirect to /login).

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) throw new Error('No refresh token');
  const { data } = await refreshClient.post<RefreshResponse>('/auth/refresh', {
    refreshToken,
  });
  tokenStore.setAccess(data.accessToken);
  if (data.refreshToken) tokenStore.setRefresh(data.refreshToken);
  return data.accessToken;
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const isAuthPath =
      typeof original?.url === 'string' && original.url.includes('/auth/');

    if (status === 401 && original && !original._retry && !isAuthPath) {
      original._retry = true;
      try {
        // Coalesce concurrent refreshes into one network call.
        if (!refreshPromise) {
          refreshPromise = performRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        const headers = AxiosHeaders.from(original.headers);
        headers.set('Authorization', `Bearer ${newToken}`);
        original.headers = headers;
        return api(original);
      } catch {
        tokenStore.clear();
        onUnauthorized?.();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

// Extracts a human-readable message from an axios error.
export function toErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string; error?: string }
      | string
      | undefined;
    if (typeof data === 'string') {
      if (data.trim()) return data;
    } else if (data) {
      if (data.message) return data.message;
      if (data.error) return data.error;
    }
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}
